import { ReactToPhaserListeners, NotifyReactEvents, RemoveListener } from 'Phaser/EventBus2';
import RoundedRect from '../GameObjects/RoundedRect';
import Note, { getNoteLengthInBeats, NoteData } from '../GameObjects/Note';
import Cursor, { StaffNote } from '../GameObjects/CursorLegacy';
import { Result } from '../GameObjects/Progress';
import ExerciseBase, { IObjective, IConfig } from './ExerciseBase';
import Transition from 'Phaser/Rendering/Transitions';
import TimeSignature from 'Models/TimeSignature';
import { INoteGraphicsDatabase } from './StaffExercise';
import IPlayable from 'Phaser/GameObjects/Playable';
import Rest from 'Phaser/GameObjects/Rest';
import { COLORBLIND_TYPE, isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { SuspendGuard } from 'Utils/suspendGuard';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';

interface NoteSequenceObjective extends IObjective {
  scale?: number;
  notes: NoteData[];
}

export interface Config extends IConfig<NoteSequenceObjective> {
  transitionDelay?: number;
  tempo: number;
  paddingVertical?: number;
  paddingHorizontal?: number;
  transition?: 'Wipe' | 'Dissolve';
  cbType?: COLORBLIND_TYPE;
}

export function getDurationFromBeats(numBeats: number, tempo: number): number {
  const msPerBeat = (60 / tempo) * 1000;
  return numBeats * msPerBeat;
}

export default class NoteSequenceExercise
  extends ExerciseBase<NoteSequenceObjective, Config>
  implements INoteGraphicsDatabase
{
  SPACE?: Phaser.Input.Keyboard.Key;

  // The configuration parameters with which to create the exercise.
  config?: Config;
  // The calculated available screen area excluding padding and other static game objects.
  playAreaSize?: { width: number; height: number; x: number; y: number };

  // State
  // The index of the current note that the player should play.
  currentNote?: number;
  cursor: Cursor | null = null;
  // Interval handle for the metronome.
  //clickIntervalHandle: Phaser.Time.TimerEvent | null = null;
  // Timer handle for delaying the start of the practice by a few beats.
  startTimerHandle: number | null = null;
  // Tween handle for the current progress indicator.
  tweenHandle?: Phaser.Tweens.Tween;
  /* Set to true if the player has hit the note slightly early.
   * It is reset to false automatically after the buffer window expires.
   * It is also reset to false once its been used to mark a note as held.
   * Must be used only via the `getEarlyHitBuffer()` and `setEarlyHitBuffer()` methods.
   */
  earlyHitBuffer: boolean = false;
  earlyHitBufferResetTimer: Phaser.Time.TimerEvent | null = null;
  private nativeMetronomeStarted = false;

  // Timer handles
  // Timer handle for marking a note as `missing` if the user takes too long to play it.
  lateHoldTimer: number | null = null;
  // Timer handle for marking a note as `early` if the user releases it early.
  earlyReleaseTimer: number | null = null;
  // Timer handle for marking a note as either `missed` if the user didnt hold it at all
  // during its entire duration, or `done` if the user held it for its entire duration.
  noteEndTimer: number | null = null;

  // Tween Handles
  cursorTweenHandle?: Phaser.Tweens.Tween;

  // Objects
  noteGraphics: (Note | Rest)[] = [];
  progressIndicators: RoundedRect[] = [];
  currentProgressIndicator: RoundedRect | null = null;
  results: (Result | undefined)[] = [];

  running: boolean = false;

  // MUS-552: guards against the free-running audio clock false-completing this
  // exercise on a device suspend/resume (same bug class MUS-175 fixed for
  // StaffExercise). Sampled each active frame in update(), re-baselined at
  // play-start and on resume.
  private readonly suspendGuard = new SuspendGuard();
  passFailIndicator?: Phaser.GameObjects.Image;

  started: boolean = false;
  timers: Phaser.Time.TimerEvent[] = [];

  constructor() {
    super('NoteSequenceExercise');
  }

  init(config: Config) {
    this.config = config;
    this.nativeMetronomeStarted = false;
    this.currentNote = -1;
    this.results = [];
    this.noteGraphics = [];
    this.progressIndicators = [];
    this.config!.paddingHorizontal ??= 50;
    this.config!.paddingVertical ??= 50;
    super.init(config);
    this.currentObjective = -1;
    /*this.timers.push(
      this.time.delayedCall(
        this.config!.transitionDelay ?? 500,
        () => {
          this.transition2();
          this.instantiateObjects();
        },
        [],
        this
      )
    );*/
  }

  skipLoadTransition() {
    if (this.config!.transition === 'Dissolve') this.fade(false, undefined, undefined, true);
    else this.wipe(false, undefined, undefined, true);
  }

  unloadGraphics() {
    this.started = false;
    this.cursor?.destroy();
    this.noteGraphics.forEach((note) => note.destroy());
    this.noteGraphics.clear();
    this.cursor = null;
    this.running = false;
    this.stopNativeMetronome();
  }

  unload(): void {
    console.warn('unloading chapter ', this.config!.chapterIdx!);
    this.timers.forEach((timer) => timer.remove());
    this.timers.clear();
    this.unloadGraphics();
    this.removeOwnListener('UpdateObjective');
    super.unload();
  }

  midiOn(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MIDI callback payload
    _data: any,
  ): void {
    // MUS-552: a paused exercise takes no keys (as StaffExercise), so a key can
    // never start a run that update() is holding still.
    if (this.paused) return;
    if (this.started) {
      this.playNote();
    }
  }

  midiOff(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MIDI callback payload
    _data: any,
  ): void {
    if (this.started) {
      this.releaseNote();
    }
  }

  transition2(out?: boolean, onDone?: () => void, scope?: object) {
    if (this.config!.transition === 'Dissolve') {
      this.fade(out, onDone, scope);
    } else {
      this.wipe(out, onDone, scope);
    }
  }

  wipe(reverse?: boolean, onDone?: () => void, scope?: object, skip?: boolean) {
    const targets = this.cameras.main.getPostPipeline(
      this.config!.transition ?? 'Wipe',
    ) as Transition;
    this.tweens.add({
      targets,
      progress: reverse ? 0.0 : 1.0,
      duration: skip ? 10 : 1000,
      onComplete: onDone,
      callbackScope: scope,
    });
  }

  fade(reverse?: boolean, onDone?: () => void, scope?: object, skip?: boolean) {
    if (reverse) {
      const targets = this.cameras.main.getPostPipeline(
        this.config!.transition ?? 'Dissolve',
      ) as Transition;
      this.tweens.add({
        targets,
        progress: 0.0,
        duration: skip ? 10 : 1000,
        onComplete: onDone,
        callbackScope: scope,
      });
    } else {
      for (let i = 0; i < this.noteGraphics.length; i++) {
        if (skip) this.noteGraphics[i].setAlpha(1);
        else {
          if (i === this.noteGraphics.length - 1) {
            this.noteGraphics[i].fadeIn(600, i * 300, onDone, scope);
          } else {
            this.noteGraphics[i].fadeIn(600, i * 300);
          }
        }
      }
    }
  }

  loadObjective(objectiveIndex: number): void {
    this.currentObjective = objectiveIndex;
    const objective = this.config?.objectives[objectiveIndex];
    if (!objective) return;

    this.showInstruction(objective.text ?? 'Play these notes.');
    this.instantiateObjects(true);
    if (objectiveIndex > 0) this.skipLoadTransition();
    else setTimeout(() => this.transition2(false), 50);
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
  }

  private startNativeMetronome(bpm: number) {
    if (!isCapacitorNative() || this.nativeMetronomeStarted) return;
    this.nativeMetronomeStarted = true;
    MuseFlowAudio.setMetronomeEnabled({ enabled: true });
    MuseFlowAudio.setDownbeatsEnabled({ enabled: true });
    MuseFlowAudio.startPlayback({
      bpm,
      timeSigNumerator: 4,
      timeSigDenominator: 4,
      countInMeasures: 0,
      startMeasure: 0,
      tickNoteType: NoteType._32nd,
    });
  }

  private stopNativeMetronome() {
    if (!isCapacitorNative() || !this.nativeMetronomeStarted) return;
    this.nativeMetronomeStarted = false;
    MuseFlowAudio.pausePlayback();
  }

  initExercise(_objectiveIndex: number): void {
    this.started = true;
    // reset every state variable and game objects to their initial states, in case of restarts
    this.currentNote = -1;
    if (this.cursor && !this.cursor.isActive()) {
      this.cursor.reset();
      this.cursor.waitForInput();
    }
    for (const note of this.noteGraphics) {
      note.reset();
    }
    for (let i = 0; i < this.results.length; i++) {
      this.results[i] = undefined;
    }
    if (this.passFailIndicator) this.passFailIndicator.destroy();
  }

  restart() {
    this.dispatchExerciseEvent({ type: 'RETRY' });
  }

  // Always use this function exclusively for setting the early hit buffer.
  // Do not mutate any related variables directly!
  setEarlyHitBuffer() {
    if (this.earlyHitBufferResetTimer) this.earlyHitBufferResetTimer.remove();
    this.earlyHitBuffer = true;
    this.earlyHitBufferResetTimer = this.time.addEvent({
      callback: () => (this.earlyHitBuffer = false),
      delay: 200,
      callbackScope: this,
    });
  }

  // Always use this function exclusively for clearing the early hit buffer.
  // Do not mutate any related variables directly!
  clearEarlyHitBuffer() {
    if (this.earlyHitBufferResetTimer) this.earlyHitBufferResetTimer.remove();
    this.earlyHitBuffer = false;
    this.earlyHitBufferResetTimer = null;
  }

  // This is the only way of accessing the early hit buffer without breaking it.
  // The buffer is cleared right after it is read since it should only be `consumed` once,
  // before it is set again.
  getEarlyHitBuffer(): boolean {
    const val = this.earlyHitBuffer;
    this.earlyHitBuffer = false;
    if (this.earlyHitBufferResetTimer) this.earlyHitBufferResetTimer.remove();
    this.earlyHitBufferResetTimer = null;
    return val;
  }

  onExerciseEnd() {
    this.running = false;
    /*if (this.clickIntervalHandle) {
      this.clickIntervalHandle.remove();
      this.clickIntervalHandle = null;
    }*/
    if (this.cursor) {
      if (this.cursorTweenHandle && this.cursorTweenHandle.isPlaying())
        this.cursorTweenHandle.stop();
      if (this.tweenHandle && this.tweenHandle.isPlaying()) this.tweenHandle.stop();
    }
    // We consider the exercise a fail only if atleast one of the notes
    // were missed COMPLETELY. Every other scenario leads to a pass including notes
    // that were played off-time or released too early.
    console.debug(this.results);
    this.dispatchExerciseEvent({ type: 'PHRASE_END' });
    NotifyReactEvents.ExerciseEnded();
  }

  // Should return true if all notes have a Successful result
  checkNotes = (): boolean => {
    console.debug(this.noteGraphics);
    this.noteGraphics.forEach((note) => console.log('result: ', note.getResult()));
    return (
      this.noteGraphics.find((note) => {
        const result = note.getResult();
        console.debug('note result: ', result);
        return result === Result.Missed || result === Result.OffTime || result === undefined;
      }) === undefined
    );
  };

  onExerciseOver(): void {
    //this.cursor?.destroy();
    super.onExerciseOver();
  }

  evaluatePerformance(): void {
    if (this.checkNotes()) {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
    } else {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_FAILED' });
    }
  }

  stopPlayback(): void {
    this.started = false;
    this.cursor?.stop();
    this.stopNativeMetronome();
  }

  pauseScene(): void {
    this.cursor?.pause();
    this.paused = true;
    this.scene.launch('PauseMenu');
    NotifyReactEvents.ExercisePaused({});
  }

  resumeScene(): void {
    this.paused = false;
    this.suspendGuard.reset(); // MUS-552: consume the away-gap; next real sleep still caught
    this.cursor?.resume();
    NotifyReactEvents.ExerciseResumed();
  }

  onObjectivePassed() {}

  onExerciseFail() {
    NotifyReactEvents.ExerciseFail();
  }

  instantiateObjects(_forceLoad?: boolean) {
    const {
      notes,
      text: _text,
      instructionWaitTime: _instructionWaitTime,
      scale,
    } = this.config!.objectives[Math.max(this.currentObjective!, 0)];
    /*if (instructionWaitTime && instructionWaitTime !== Infinity) {
      this.instructionTimeout = setTimeout(
        () => this.showInstruction(instructionText, 'Top'),
        time
      );
    }*/
    const dpr = 1;
    const paddingH = (this.config!.paddingHorizontal ?? 50) * dpr;
    const paddingV = (this.config!.paddingVertical ?? 50) * dpr;
    this.playAreaSize = {
      width: this.cameras.main.width - paddingH * 2,
      height: this.cameras.main.height - paddingV * 2,
      x: paddingH,
      y: paddingV,
    };
    /*const rect = this.add.rectangle(this.playAreaSize.x, this.playAreaSize.y, this.playAreaSize.width, this.playAreaSize.height, 0xff0000, 0.5);
    rect.setOrigin(0, 0);
    rect.setPosition(this.playAreaSize.x, this.playAreaSize.y);*/
    const yMid = this.playAreaSize.height / 2 + paddingV;
    // we want to centre-align the notes horizontally but not based on the entire length of the
    // note (the width of the note sprite + the width of the tail) so we have to offset the entire
    // group by the width of the final note.
    // A simple way of acheiving that is by simply adding the width of the last note twice
    // to the length of the entire thing before offsetting to make sure that the final tail
    // doesn't go beyond the width of the parent container.
    const numBeats = notes.reduce((acc, curr) => acc + getNoteLengthInBeats(curr.length), 0);
    const lastNoteBeats = getNoteLengthInBeats(notes.last().length);
    const centreTails = notes.last().length === 'whole';
    const beatWidth = this.playAreaSize.width / (numBeats! + (!centreTails ? lastNoteBeats : 0));
    const lastNoteWidth = beatWidth * lastNoteBeats;
    const offset = this.playAreaSize.x + (!centreTails ? lastNoteWidth : 0);
    const cursorWidth = 65 * dpr * (scale ?? 1);
    const cursorHeight = 375 * dpr * (scale ?? 1);
    notes.reduce((acc, note, idx) => {
      const width = beatWidth * getNoteLengthInBeats(note.length);
      const obj = note.isRest
        ? new Rest(
            this,
            acc,
            yMid + cursorHeight / 4,
            note.length,
            this.config!.tempo,
            idx,
            false,
            this.config!.cbType,
          )
        : new Note(
            this,
            acc,
            yMid + cursorHeight / 3,
            width,
            note.length,
            this.config!.tempo,
            'up',
            idx,
            false,
            this.config!.cbType,
            //this.config!.transition === "Dissolve" // spawn all notes with alpha 0 for dissolve transition
          );
      obj.setScale(scale ?? 1);
      this.noteGraphics.push(obj);
      this.results.push(undefined);
      return acc + beatWidth * getNoteLengthInBeats(note.length);
    }, offset);
    for (let i = 0; i < this.noteGraphics.length - 1; i++) {
      const widthOffset = this.noteGraphics[i + 1].getWidth() * 0.55;
      if (!this.noteGraphics[i].isRest)
        (this.noteGraphics[i] as Note).setProgressIndicatorMaxWidth(widthOffset);
    }
    const cursorData: StaffNote[] = this.noteGraphics.map((note) => {
      return {
        position: { x: note.x - cursorWidth / 2, y: note.y - cursorHeight / 2 },
        durationInMs: note.getDurationMs(),
        durationRelative: getNoteLengthInBeats(note.length) / 4.0,
        playable: note,
        isRest: note.isRest,
      };
    });
    cursorData.push({
      position: {
        x: this.playAreaSize.width + this.playAreaSize.x,
        y: 0,
      },
      durationInMs: 0,
      durationRelative: 0,
    });
    this.cursor = new Cursor(
      this,
      offset - cursorWidth * 0.5,
      yMid - cursorHeight / 2,
      cursorWidth,
      cursorHeight,
      cursorWidth * 0.5,
      new TimeSignature(4, 4),
      this.config!.tempo,
      cursorData,
    );
  }

  getNoteById(_id: string): IPlayable | null {
    return null;
  }

  getNotePosition(_id: string) {
    return 0;
  }

  getPhraseEndPosition() {
    return 0;
  }

  playNote() {
    console.log('playing note');
    this.setEarlyHitBuffer();
    if (!this.running) {
      const [timing] = this.cursor!.tryStart(this.onExerciseEnd, this);
      if (timing !== 'perfect') {
        this.noteGraphics[0].shake(Math.min((60 / this.config!.tempo) * 1000, 200));
        if (timing === 'early') {
          this.showToast('Too early');
        } else if (timing === 'late') {
          this.showToast('Too late');
        }
      } else {
        for (let i = 1; i < this.noteGraphics.length; i++) {
          this.noteGraphics[i].reset(true);
        }
        this.running = true;
        this.suspendGuard.reset(); // MUS-552: baseline at play-start so frame 1 never pauses
        this.currentNote = 0;
        //this.noteGraphics[0].setFocus(true);
      }
    }
    if (this.running) {
      this.cursor?.onHoldNote();
    }
  }

  releaseNote() {
    this.clearEarlyHitBuffer();
    if (this.running) {
      this.cursor?.onReleaseNote();
    }
  }

  pauseExercise() {
    this.cursor?.pause();
    super.pauseExercise();
  }

  resume() {
    this.cursor?.resume();
    super.resume();
  }

  update(time: number) {
    // MUS-552: hold every frame while paused, not only the one that paused.
    // CursorLegacy.update() moves the cursor and fires its end callback outside
    // its own `paused` checks, and its resume compensation assumes it was not
    // called during the pause, so a paused scene must never reach it.
    if (this.paused) return;
    // MUS-552: a device suspend/resume produces a single frame whose wall-clock
    // delta is the whole away-duration. CursorLegacy.update() advances off the
    // free-running audio clock and completes the exercise when it runs past the
    // last note, so left unguarded that jump false-completes with no user input.
    // Sample only while actively playing and not paused; when the jump lands,
    // pause and return BEFORE cursor.update() so the completion check never runs
    // (prevented, not just detected). Resume re-anchors the clock and re-baselines
    // the guard, so the stale delta is discarded there.
    if (this.running && this.suspendGuard.sample()) {
      this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
      return;
    }
    this.cursor?.update(time);
    if (this.running) {
      if (this.currentNote! < this.noteGraphics.length - 1) {
        if (
          this.cursor!.isMoving &&
          this.cursor!.x + this.cursor!.width / 2 > this.noteGraphics[this.currentNote! + 1].x
        ) {
          this.currentNote! += 1;
        }
      }
    }
  }

  onNoteComplete(result: Result, idx: number) {
    console.log(result);
    this.results[idx] = result;
    if (idx === this.noteGraphics.length - 1) {
      this.onExerciseEnd();
    }
  }

  create() {
    // This event must be emitted before using any of the integrated phaser modules such as tweens, physics, etc.
    this.cameras.main.setPostPipeline(this.config!.transition || 'Wipe');
    if (this.config!.transition === 'Dissolve') {
      (this.cameras.main.getPostPipeline('Dissolve') as Transition).progress = 1;
    }
    NotifyReactEvents.CurrentSceneReady(this);
  }

  stop() {
    this.scene.stop();
  }
}
